// @givmo/app-admin — Settings → Security → Company sign-in → DIRECTORY SYNC (AS-WEB7).
//
// The employer's SCIM switch, the endpoint URL their identity provider posts to, and the bearer
// token that authenticates it. Three acts: turn sync on, turn it off, replace the token.
//
// ══ ★ THE ONE THING THIS FILE EXISTS TO GET RIGHT ═════════════════════════════════════════════
// A BEARER TOKEN EXISTS EXACTLY ONCE — IN THE RESPONSE THAT MINTED IT. The broker stores a hash, so
// there is nothing to read back, no route that could return it, and no "reveal" affordance to add
// later: there would be nothing to reveal. Two consequences shape everything below.
//
//   1. THE VALUE NEVER LEAVES COMPONENT STATE. It is not merged into the query cache, not written to
//      storage, not logged, and not passed to a parent. A cache write in particular would be the
//      quiet failure — the token would survive this panel's unmount and re-render on a later visit,
//      turning a show-once credential into a retrievable one without anybody choosing that.
//   2. THE SCREEN NEVER IMPLIES IT CAN BE FETCHED AGAIN. `has_bearer_token: true` with no token is
//      the ordinary "already set up" state, and the honest thing to render there is the server's own
//      sentence saying so — not a disabled Show button, not a masked placeholder standing in for a
//      value we do not have. An Owner who has lost theirs rotates.
//
// ══ TWO FACTS, TWO GATES — `available` DECIDES EXISTENCE, `enabled` DECIDES POSITION ══════════
// ★ `scim.available` is a property of the DEPLOYMENT (`EMPLOYER_SCIM_ENABLED`); `scim.enabled` is a
// property of the EMPLOYER (their Owner's switch). They are never collapsed, because they want
// opposite UI: `available: false` means this card should not exist — every write would 404 — while
// `enabled: false` means the card exists and its switch is off. A reader that treated either `false`
// as "off" would put a directory-sync control in front of an employer who cannot use it.
//
// The field exists because this sub-surface has no probe of its own. Its two routes carry the SSO
// router's `EMPLOYER_SSO_ENABLED` PLUS a route-level `EMPLOYER_SCIM_ENABLED`, and a client cannot
// ask about the inner one: both routes are write-only, and a `GET` on the PUT-only path answers 405,
// raised before route dependencies run. So the server states it on the read the section already
// makes (`agent_docs/admin-settings-tabs.md` § *Which dark gate* holds the general shape).
//
// ⚠ THE 404 LATCH STAYS, AND IS NOT REDUNDANT WITH THE FIELD. The flag can flip between the read and
// the click, and a mid-session rollback is exactly when an Owner is pressing things. In that race the
// card is already on screen, so withdrawing it entirely would read as a crash: the controls withdraw
// and {@link SSO_COPY_DRAFT.scimUnavailable} says why. Known-unavailable at read time and
// became-unavailable mid-interaction are different moments and get different treatment on purpose.
//
// ══ THE TWO DIRECTIONS ARE NOT SYMMETRIC, AND THE CONTROL SHOWS IT ════════════════════════════
// Enabling needs a verified domain AND a configured connection and REFUSES without them; disabling
// is unconditional, makes no broker call, and destroys nothing. So the switch is disabled only in
// the ON direction and only while `can_enable_sso` is false — the one act that stops a sweep from
// deleting employment rows can never be blocked by this screen.
//
// ══ WHY THE WRITES MERGE AND ONLY THE DISMISS RE-READS ════════════════════════════════════════
// The domain and connection steps invalidate the whole config after every write, because
// `can_enable_sso` is DERIVED server-side from the verification stamp and the connection and so
// changes outside the block they merged. A SCIM write derives nothing: it sets `company.scim_enabled`
// and touches no other field in `SsoConfigOut`. A refetch on the write would cost a round trip whose
// only effect is to re-null the broker-side fields (the read makes no broker call). So the scrubbed
// block is merged and the write itself invalidates nothing.
//
// ⚠ THE ONE EXCEPTION IS DISMISSING A REVEAL, and it is not symmetry-for-its-own-sake. A mint's
// `setup_guidance` is bound to the TOKEN's lifetime — "copy it now, it is shown once" — while the
// merge writes it into the cache, where it outlives the value it describes. Without the re-read,
// closing the panel leaves the card instructing an Owner to copy a token that is no longer on
// screen. Only the server can state the post-mint truth, so only there is it asked.

import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Switch,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { SSO_COPY, SSO_COPY_DRAFT } from './security-copy';
import { copyToClipboard } from './copy-to-clipboard';
import {
  directoryKnownAbsent,
  readScim as decodeScim,
  type ScimState,
  type SsoConfigState,
} from './sso-decode';
import { rotateScimToken, saveScimSettings } from './sso-config-transport';
import { SsoErrorNote } from './sso-error-note';
import { WRAPPING_CARD_TITLE } from './card-title';

const PREFIX = 'settings-security-sso-scim';

export interface ScimPanelProps {
  config: SsoConfigState;
  /**
   * Merge a write's `scim` block back into the cached config, so the switch reflects the stored
   * value immediately rather than for one render showing the position the write just changed.
   *
   * ⚠ THE CALLER MUST RECEIVE A SCRUBBED BLOCK. This panel never hands it one carrying
   * `bearer_token` — see the header, consequence 1.
   */
  mergeScim: (block: unknown) => void;
  /** Re-read the whole config. Called ONLY when a token reveal is dismissed — see that call site. */
  invalidateConfig: () => void;
}

/** What a mint produced. Held together because the guidance is only correct beside its own token. */
interface Minted {
  token: string;
  /** The server's `setup_guidance` AS IT ARRIVED WITH THE TOKEN — the show-once warning rides it. */
  guidance: string | null;
}

export function ScimPanel({ config, mergeScim, invalidateConfig }: ScimPanelProps) {
  const read = config.scim;
  const labelId = useId();

  // The broker-side fields, which the configuration read never carries. Sourced only from a write
  // response and deliberately NOT invalidated away — losing the endpoint URL to a refetch in the
  // middle of setting up an identity provider is the failure this outlives.
  const [written, setWritten] = useState<ScimState | null>(null);
  // ★ The show-once value. Component state, cleared on dismiss, gone on unmount. Nothing else.
  const [minted, setMinted] = useState<Minted | null>(null);
  const [dark, setDark] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const directoryConfigured = written?.directoryConfigured ?? read.directoryConfigured;
  const baseUrl = written?.scimBaseUrl ?? read.scimBaseUrl;

  /**
   * Take a write response.
   *
   * The scrub is at the SEAM rather than at each call site on purpose: every path out of this panel
   * that could put the token somewhere durable goes through here, so there is one line to read to
   * know it cannot.
   */
  const settle = (body: unknown) => {
    const next = decodeScim(body);
    // The broker-side fields are what `written` exists to carry; the TOKEN is not among them. Scrubbed
    // here so "dismiss destroys the value" is literally true rather than true-in-practice — without
    // it the value sits in component state until unmount, unrendered but present.
    setWritten({ ...next, bearerToken: null });
    setDark(false);
    setForbidden(false);
    mergeScim({ ...(typeof body === 'object' && body !== null ? body : {}), bearer_token: null });
    if (next.bearerToken !== null) {
      setCopyFailed(false);
      setMinted({ token: next.bearerToken, guidance: next.setupGuidance });
    }
  };

  /** 404 = the SCIM half is dark here; 403 = a Member reached a write. Both withdraw the controls. */
  const classify = (error: unknown) => {
    if (isGivmoError(error) && error.status === 404) setDark(true);
    if (isGivmoError(error) && error.status === 403) setForbidden(true);
  };

  const setEnabled = useMutation({
    // ⚠ `gcTime: 0` IS PART OF THE SHOW-ONCE GUARANTEE, not a performance knob. TanStack stores a
    // mutation's resolved body as `state.data` in the SHARED QueryClient's MutationCache, which
    // outlives this component by the default 5-minute `gcTime` — so a minted token would sit in a
    // durable cache for five minutes after the Owner believed they had destroyed it, reachable by
    // anything that can read the client (devtools, a memory dump on a shared machine). Nothing in the
    // UI can retrieve it, but "the value never leaves component state" would simply not be true.
    // Zero evicts the entry the moment the panel unmounts. Pinned by the whole-cache durability test,
    // which sweeps the MUTATION cache as well as the query cache.
    gcTime: 0,
    mutationFn: (enabled: boolean) => saveScimSettings({ enabled }),
    onSuccess: settle,
    onError: classify,
  });

  const rotate = useMutation({
    // Same reason as the enable above — a rotation mints too, so its response is the same credential.
    gcTime: 0,
    mutationFn: rotateScimToken,
    onSuccess: (body) => {
      setConfirmingRotate(false);
      settle(body);
    },
    onError: classify,
  });

  const busy = setEnabled.isPending || rotate.isPending;
  const withdrawn = dark || forbidden;

  // ★ THE DEPLOYMENT GATE, AND IT RENDERS NOTHING — not a card, not a notice. An employer in an
  // environment that does not serve SCIM should never meet the concept, exactly as the SSO section
  // and the MFA editor render nothing when their own probes come back closed. `read.enabled` is not
  // consulted here and must not be: a stored `true` under `available: false` says nothing about what
  // this deployment can do, and the card it would produce has no working control on it.
  //
  // Deliberately AFTER the hooks. Returning before them would change the hook order between renders
  // the moment a refetch flipped the field, which React treats as a crash rather than a re-render.
  if (!read.available) return null;

  return (
    <Card>
      <CardHeader
        className={WRAPPING_CARD_TITLE}
        title={SSO_COPY_DRAFT.scimTitle}
        subtitle={SSO_COPY_DRAFT.scimSubtitle}
      />
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp(PREFIX)}>
          {/* The consequence, before the switch rather than after it. `can_enable_sso` is the same
              pair of preconditions the tier needs, so the note names the steps that satisfy them. */}
          {!config.canEnableSso && !read.enabled && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-needs-setup`)}
            >
              {SSO_COPY_DRAFT.scimNeedsSetup}
            </p>
          )}

          {!withdrawn && (
            <div className="flex items-start gap-12">
              <Switch
                appearance="saas"
                checked={read.enabled}
                // The VISIBLE label is the accessible name, so the two can never drift. `Switch`
                // takes no `label` prop and is not wrapped in one here, so pointing at the text is
                // the association — an `aria-label` would be a second copy of the same sentence.
                aria-labelledby={labelId}
                // ⚠ ASYMMETRIC BY DESIGN. Blocked only in the ON direction, and only on the missing
                // preconditions — turning sync OFF is unconditional server-side and is the act that
                // stops a sweep deleting employment rows, so this screen never stands in its way.
                disabled={busy || (!read.enabled && !config.canEnableSso)}
                onCheckedChange={(next) => setEnabled.mutate(next)}
                testId={`${PREFIX}-switch`}
              />
              <div className="flex flex-col gap-4">
                <span
                  id={labelId}
                  className="font-sans text-s14 font-w500 tracking-givmo text-text-strong"
                >
                  {SSO_COPY_DRAFT.scimSwitchLabel}
                </span>
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary">
                  {SSO_COPY_DRAFT.scimSwitchHelp}
                </span>
                {/* AS-BE38 (web) — the employer's side of "unlinking cancels pending match requests".
                    A STANDING note rather than a confirmation line, because a directory deprovision
                    happens in the identity provider and no Givmo dialog ever sees it. Beside the
                    switch's own consequence, whatever the switch's position: it is part of what
                    turning sync on means. */}
                <span
                  className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary text-pretty"
                  {...testIdProp(`${PREFIX}-match-requests-note`)}
                >
                  {SSO_COPY.scimMatchRequestsNote}
                </span>
              </div>
            </div>
          )}

          {/* The server's own state-matched guidance, verbatim. It is the only thing on this screen
              that knows whether a directory already existed, and it is where the "we cannot show you
              the old token" sentence lives — which is why nothing here paraphrases it.

              ⚠ SUPPRESSED WHILE THE REVEAL IS OPEN. A mint merges its response into the cache, so
              this paragraph and the reveal panel's body become the SAME sentence rendered twenty
              pixels apart — which is what it looked like on screen before this line existed. While
              the panel is up it owns the next step; this returns when it closes. */}
          {!withdrawn && minted === null && read.setupGuidance !== null && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-guidance`)}
            >
              {read.setupGuidance}
            </p>
          )}

          {/* Only a write ever carries the URL, so for the whole of an ordinary page load this row is
              absent. Saying nothing is the honest state; a placeholder would read as a value. */}
          {!withdrawn && baseUrl !== null && (
            <CopyableValue
              label={SSO_COPY_DRAFT.scimBaseUrlLabel}
              value={baseUrl}
              copyLabel={SSO_COPY_DRAFT.scimBaseUrlCopy}
              copiedLabel={SSO_COPY_DRAFT.scimBaseUrlCopied}
              onCopyFailed={setCopyFailed}
              testId={`${PREFIX}-base-url`}
            />
          )}

          {minted !== null && (
            <TokenReveal
              minted={minted}
              copyFailed={copyFailed}
              onCopyFailed={setCopyFailed}
              // ⚠ THE DISMISS RE-READS THE CONFIG, and this is the one place on the card that must.
              // A mint's `setup_guidance` is bound to the TOKEN's lifetime — "copy it now, it is
              // shown once" — but the merge writes it into the cache, where it outlives the value it
              // describes. Left alone, closing the panel would leave the card telling an Owner to
              // copy a token that is no longer on screen. The server is the only thing that can
              // state the post-mint truth ("already set up, and we cannot show you the old value"),
              // so it is asked. The refetch cannot cost the endpoint URL: that is held in `written`,
              // not in the cache the refetch replaces.
              onDismiss={() => {
                setMinted(null);
                invalidateConfig();
              }}
            />
          )}

          {/* ⚠ NOT WHILE THE REVEAL IS OPEN — it carries its own copy-failed sentence, and the two share
              one `copyFailed` state, so an ungated note renders the same sentence twice. The same
              duplication class as the guidance above, which is suppressed for the same reason. */}
          {copyFailed && minted === null && (
            <p
              className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary"
              {...testIdProp(`${PREFIX}-copy-failed`)}
            >
              {SSO_COPY_DRAFT.scimCopyFailed}
            </p>
          )}

          {/* ⚠ NOT WHILE WITHDRAWN. The 404 and the 403 are what SET `withdrawn`, and each already
              has its own considered notice below — rendering the server's raw sentence too would put
              a red "Company sign-in is not available" above a calm "not available on this
              environment yet", which is two contradictory answers to one press. It looked exactly
              that way on screen. The failures worth showing here are the ones an Owner can act on. */}
          {!withdrawn && setEnabled.isError && (
            <SsoErrorNote
              error={setEnabled.error}
              // Which direction failed decides the fallback, because "could not turn on" and "could
              // not turn off" are opposite states and an Owner needs to know which one they are in.
              fallback={
                setEnabled.variables === true
                  ? SSO_COPY_DRAFT.scimEnableFailed
                  : SSO_COPY_DRAFT.scimDisableFailed
              }
              testId={`${PREFIX}-switch-error`}
            />
          )}

          {!withdrawn && !directoryKnownAbsent({ ...read, directoryConfigured }) && (
            <RotateControl
              confirming={confirmingRotate}
              pending={rotate.isPending}
              error={rotate.isError ? rotate.error : null}
              onAsk={() => setConfirmingRotate(true)}
              onCancel={() => setConfirmingRotate(false)}
              onConfirm={() => rotate.mutate()}
            />
          )}

          {dark && (
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-secondary"
              {...testIdProp(`${PREFIX}-unavailable`)}
            >
              {SSO_COPY_DRAFT.scimUnavailable}
            </p>
          )}

          {forbidden && (
            <p
              className="font-sans text-s13 font-w500 tracking-normal text-danger-700"
              {...testIdProp(`${PREFIX}-forbidden`)}
            >
              {SSO_COPY.forbidden}
            </p>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * The show-once panel.
 *
 * ★ IT HAS NO WAY BACK, AND THAT IS THE FEATURE. Dismiss clears the value from the only place it
 * exists; navigation and a refresh do the same thing without being asked. There is deliberately no
 * "show again", no reopen and no persistence — a control that could bring it back would mean the
 * value had been kept somewhere, which is the thing this whole surface is built not to do. The
 * dismiss LABEL names that, because the button is a one-way door.
 *
 * The body is the server's `setup_guidance` as it arrived with the token, which already carries the
 * show-once warning. The local fallback below is the single deliberate duplication on this screen:
 * everywhere else a missing server sentence costs an explanation, and here it would cost the only
 * warning standing between an Owner and a credential they cannot get back.
 */
function TokenReveal({
  minted,
  copyFailed,
  onCopyFailed,
  onDismiss,
}: {
  minted: Minted;
  copyFailed: boolean;
  onCopyFailed: (failed: boolean) => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-warning-100 bg-warning-50 p-16"
      {...testIdProp(`${PREFIX}-reveal`)}
    >
      <p className="font-sans text-s14 font-w600 tracking-givmo text-warning-700">
        {SSO_COPY_DRAFT.scimTokenRevealTitle}
      </p>
      <p
        className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-warning-700"
        {...testIdProp(`${PREFIX}-reveal-guidance`)}
      >
        {minted.guidance ?? SSO_COPY_DRAFT.scimTokenRevealFallback}
      </p>

      <CopyableValue
        label={SSO_COPY_DRAFT.scimTokenLabel}
        value={minted.token}
        copyLabel={SSO_COPY_DRAFT.scimTokenCopy}
        copiedLabel={SSO_COPY_DRAFT.scimTokenCopied}
        onCopyFailed={onCopyFailed}
        testId={`${PREFIX}-reveal-token`}
      />

      {/* The reveal's OWN copy-failed note — distinct testid from the card's, so the two are
          separable in a test and a duplicate cannot hide behind one selector. */}
      {copyFailed && (
        <p
          className="font-sans text-s12 font-w400 tracking-normal text-warning-700"
          {...testIdProp(`${PREFIX}-reveal-copy-failed`)}
        >
          {SSO_COPY_DRAFT.scimCopyFailed}
        </p>
      )}

      <div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onDismiss}
          testId={`${PREFIX}-reveal-dismiss`}
        >
          {SSO_COPY_DRAFT.scimTokenDismiss}
        </Button>
      </div>
    </div>
  );
}

/**
 * The rotation control and its confirm step.
 *
 * ⚠ OFFERED EVEN WHILE SYNC IS OFF, AND EVEN WHEN WE CANNOT SEE A DIRECTORY. The token authenticates
 * the employer's identity provider to the broker whether or not Givmo is reading the directory, so
 * withholding the rotation until an Owner first ARMS a sweep that deletes employment rows would
 * refuse a security act on the grounds of an unrelated preference — which is the reasoning the
 * backend's own exception carries. The configuration read never says whether a directory exists
 * (`directory_configured` is `null` there, meaning "not read"), so the control is withdrawn only
 * when a write has positively said there is none; where there genuinely is none the backend answers
 * a typed `SSO_SCIM_NOT_CONFIGURED` whose sentence names the remedy, which beats a hidden control.
 *
 * The confirm is an inline block rather than a dialog, matching the domain re-mint beside it: both
 * are destructive-but-recoverable single acts, and a modal for each would make the section a queue of
 * interruptions.
 */
function RotateControl({
  confirming,
  pending,
  error,
  onAsk,
  onCancel,
  onConfirm,
}: {
  confirming: boolean;
  pending: boolean;
  error: unknown;
  onAsk: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      <p className="font-sans text-s13 font-w400 leading-h15 tracking-normal text-text-tertiary">
        {SSO_COPY_DRAFT.scimRotateHelp}
      </p>

      {error !== null && (
        <SsoErrorNote
          error={error}
          fallback={SSO_COPY_DRAFT.scimRotateFailed}
          testId={`${PREFIX}-rotate-error`}
        />
      )}

      {confirming ? (
        <div
          className="flex flex-col items-start gap-8 rounded-lg border border-warning-100 bg-warning-50 p-16"
          {...testIdProp(`${PREFIX}-rotate-warning`)}
        >
          <p className="font-sans text-s13 font-w600 tracking-givmo text-warning-700">
            {SSO_COPY_DRAFT.scimRotateConfirmTitle}
          </p>
          <p className="font-sans text-s13 font-w400 leading-h15 text-warning-700">
            {SSO_COPY_DRAFT.scimRotateConfirmBody}
          </p>
          <div className="flex items-center gap-12">
            <Button
              appearance="saas"
              tone="secondary"
              onClick={onCancel}
              testId={`${PREFIX}-rotate-cancel`}
            >
              {SSO_COPY_DRAFT.scimRotateCancel}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={pending}
              onClick={onConfirm}
              testId={`${PREFIX}-rotate-confirm`}
            >
              {SSO_COPY_DRAFT.scimRotateConfirm}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onAsk}
            testId={`${PREFIX}-rotate`}
          >
            {SSO_COPY_DRAFT.scimRotate}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * A labelled value with a copy button.
 *
 * ⚠ THE COPY GOES THROUGH {@link copyToClipboard}, WHICH RETURNS A VERDICT. `writeText` returns a
 * Promise and the API is absent altogether in an insecure context, and the natural
 * `await navigator.clipboard?.writeText(v)` resolves to `undefined` in that second case — landing in
 * the success branch and telling an Owner a token was copied that never left the page. On this
 * screen that ends with a lost credential and a forced rotation. A refused clipboard is otherwise a
 * missing convenience rather than a dead end, because the value is on screen and selectable.
 */
function CopyableValue({
  label,
  value,
  copyLabel,
  copiedLabel,
  onCopyFailed,
  testId,
}: {
  label: string;
  value: string;
  copyLabel: string;
  copiedLabel: string;
  onCopyFailed: (failed: boolean) => void;
  testId: string;
}) {
  const copy = async () => {
    const copied = await copyToClipboard(value);
    onCopyFailed(!copied);
    if (copied) toast.success(copiedLabel);
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface-sunken p-16">
      <span className="font-sans text-s13 text-text-secondary">{label}</span>
      <span
        className="break-all font-mono text-s13 text-text-strong select-all"
        {...testIdProp(testId)}
      >
        {value}
      </span>
      <div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => void copy()}
          testId={`${testId}-copy`}
        >
          {copyLabel}
        </Button>
      </div>
    </div>
  );
}
